/**
 * @file Facade du core : construit le SimContext, execute les tics fixes, recoit les commandes, expose une vue lecture seule et la sauvegarde.
 */
import type { BiomeId, ModeId } from '@shared/enums';
import type { DeepReadonly, EntityId, Result } from '@shared/types';
import { buildConfigIndex, CONFIG, seasonOf } from '@config/index';
import type { HubSeason } from '@config/index';
import type { GameConfig } from '@config/index';
import type { ArenaDef, AuraStyle, ModeRules, ShotFxStyle } from '@config/types';
import type { WallClock } from './Clock';
import type { SimContext } from './Context';
import { dispatchCommand } from './commands/CommandBus';
import type { Command } from './commands/Command';
import { Transactions } from './economy/Transactions';
import { Wallet } from './economy/Wallet';
import { World } from './ecs/World';
import type { Entity } from './ecs/World';
import { EventBus } from './events/EventBus';
import type { GameEvents } from './events/GameEvents';
import { resolveControl } from './modes/ControlResolver';
import { ModeManager } from './modes/ModeManager';
import type { ModeHud } from './modes/ModeTypes';
import { Queries } from './queries';
import { hashString, RngStreams } from './rng/Rng';
import { serializeSave } from './save/serialize';
import { createServices } from './services';
import type { Services } from './services';
import type { PlayerData } from './state/PlayerData';
import type { ResolvedControl, RuntimeState } from './state/RuntimeState';
import { standardSources } from './stats/sources';
import { StatAggregator } from './stats/StatAggregator';
import { SYSTEM_PIPELINE } from './systems';
import type { System } from './systems/System';
import { SpatialGrid } from './util/SpatialGrid';
import { releaseProjectile } from './weapons/projectiles';
import { WeaponStatsCache } from './weapons/WeaponStats';

export interface SimulationOptions {
  data: PlayerData;
  clock: WallClock;
  config?: GameConfig;
  /** Graine runtime explicite (tests). Defaut : derivee de meta.seed et meta.revision. */
  runtimeSeed?: number;
  /** Pipeline alternatif (tests unitaires d'un systeme isole). */
  systems?: readonly System[];
}

/** Vue lecture seule consommee par render/ et ui/. */
export interface SimView {
  readonly tick: number;
  readonly time: number;
  readonly mode: ModeId;
  readonly rules: DeepReadonly<ModeRules>;
  readonly arena: DeepReadonly<ArenaDef>;
  readonly biome: BiomeId;
  readonly control: Readonly<ResolvedControl>;
  readonly catIds: readonly EntityId[];
  entities(): IterableIterator<DeepReadonly<Entity>>;
  entity(id: EntityId): DeepReadonly<Entity> | undefined;
  hud(): ModeHud | null;
  /** Zoom camera du hub (1 = defaut) et arme rangee (hub uniquement). */
  readonly zoom: number;
  readonly weaponStowed: boolean;
  /** Aura FX a dessiner autour du chef (null = aucune). */
  auraStyle(): AuraStyle | null;
  /** FX de tir equipe (se superpose aux skins de munitions) ; null = aucun. */
  shotFxStyle(): ShotFxStyle | null;
  /** Saison du decor du hub (date reelle, ou forcee en debug). */
  readonly season: HubSeason;
}

export class Simulation {
  readonly ctx: SimContext;
  readonly bus: EventBus<GameEvents>;
  readonly query: Queries;
  private readonly systems: readonly System[];
  private started = false;

  constructor(opts: SimulationOptions) {
    const config = opts.config ?? CONFIG;
    const data = opts.data;
    const bus = new EventBus<GameEvents>();
    const stats = new StatAggregator(config);
    const index = buildConfigIndex(config);
    const world = new World();
    const rt: RuntimeState = {
      tick: 0,
      time: 0,
      world,
      targetGrid: new SpatialGrid(config.sim.gridCellSize),
      input: { moveX: 0, moveY: 0, active: false },
      control: resolveControl(config.modes.main.rules.control, data.settings),
      catIds: [],
      biome: 'forest',
      wallMs: opts.clock.nowMs(),
      paused: false,
      hub: { zoom: 1, stowed: false, season: seasonOf(opts.clock.nowMs()), seasonOverride: null, online: false },
    };
    const modes = new ModeManager(config.modes.main.rules);
    const seed = opts.runtimeSeed ?? (data.meta.seed ^ hashString(`r${data.meta.revision}`)) >>> 0;

    // Construction en deux temps : services / tx referencent le contexte complet.
    const partial = {
      config,
      index,
      data,
      rt,
      rng: new RngStreams(seed),
      bus,
      stats,
      wallet: new Wallet(data.wallet, bus),
      clock: opts.clock,
      modes,
      weaponStats: new WeaponStatsCache(config, index, stats),
    } as Omit<SimContext, 'services' | 'tx'> & { services?: Services; tx?: Transactions };
    const ctx = partial as SimContext;
    partial.tx = new Transactions(ctx);
    partial.services = createServices(ctx);

    for (const src of standardSources(config, () => ctx.data)) stats.addSource(src);
    bus.on('ModifiersDirty', () => stats.invalidate());
    world.onDestroy = (e) => {
      rt.targetGrid.remove(e.id);
      releaseProjectile(e);
    };
    modes.bind(ctx);

    this.ctx = ctx;
    this.bus = bus;
    this.query = new Queries(ctx);
    this.systems = opts.systems ?? SYSTEM_PIPELINE;
  }

  /** Lecture seule des donnees persistantes (UI). */
  get data(): DeepReadonly<PlayerData> {
    return this.ctx.data;
  }

  /**
   * Demarrage apres construction : tutoriel, resets temporels, rapport hors-ligne, entree dans le mode Main.
   * Separe du constructeur pour permettre aux abonnes (render/ui) de s'inscrire sur le bus avant les premiers evenements.
   */
  start(): void {
    if (this.started) return;
    this.started = true;
    const s = this.ctx.services;
    const now = this.ctx.clock.nowMs();
    this.ctx.rt.wallMs = now;
    s.tutorial.init();
    this.bus.on('StageChanged', () => s.unlocks.checkNewUnlocks());
    this.bus.on('RebirthDone', () => s.unlocks.checkNewUnlocks());
    s.missions.refresh(now);
    s.pass.refreshSeason(now);
    s.offline.computeOnResume(now);
    this.ctx.modes.start('main', {}, true);
  }

  /** Un tic fixe de simulation (dt = config.sim.dt). */
  step(): void {
    const { rt, config } = this.ctx;
    const dt = config.sim.dt;
    rt.wallMs = this.ctx.clock.nowMs();
    for (const e of rt.world.all()) {
      e.transform.prevX = e.transform.x;
      e.transform.prevY = e.transform.y;
    }
    for (const sys of this.systems) {
      if (rt.paused && !sys.runWhenPaused) continue;
      sys.update(this.ctx, dt);
    }
    rt.world.flush();
    rt.tick++;
    rt.time += dt;
  }

  /** Execute n tics (tests d'equilibrage, rattrapage). */
  stepMany(n: number): void {
    for (let i = 0; i < n; i++) this.step();
  }

  dispatch(cmd: Command): Result<unknown> {
    return dispatchCommand(this.ctx, cmd);
  }

  /** Intention de deplacement (monde), appelee par app/InputBridge a chaque frame. */
  setInput(moveX: number, moveY: number, active: boolean): void {
    const i = this.ctx.rt.input;
    i.moveX = moveX;
    i.moveY = moveY;
    i.active = active;
  }

  /** Retour au premier plan apres une absence (onglet masque) : calcule le rapport hors-ligne. */
  resume(nowMs: number): void {
    this.ctx.rt.wallMs = nowMs;
    this.ctx.services.offline.computeOnResume(nowMs);
  }

  /** Marque l'instant courant comme "derniere presence" (avant mise en arriere-plan). */
  markSeen(nowMs: number): void {
    this.ctx.data.meta.lastSeenMs = nowMs;
  }

  serialize(nowMs: number): string {
    this.markSeen(nowMs);
    return serializeSave(this.ctx.data, nowMs);
  }

  /** Construit la vue a jour (accesseurs vivants, aucune copie). */
  getView(): SimView {
    const ctx = this.ctx;
    return {
      tick: ctx.rt.tick,
      time: ctx.rt.time,
      mode: ctx.modes.currentId,
      rules: ctx.modes.rules,
      arena: ctx.modes.rules.arena,
      biome: ctx.rt.biome,
      control: ctx.rt.control,
      catIds: ctx.rt.catIds,
      entities: () => ctx.rt.world.all(),
      entity: (id) => ctx.rt.world.get(id),
      hud: () => ctx.modes.getHud(),
      get zoom() {
        return ctx.modes.currentId === 'hub' ? ctx.rt.hub.zoom : 1;
      },
      get weaponStowed() {
        return ctx.modes.currentId === 'hub' && ctx.rt.hub.stowed;
      },
      auraStyle: () => ctx.services.auras.activeStyle(),
      shotFxStyle: () => ctx.services.shotFx.activeStyle(),
      get season() {
        return ctx.rt.hub.season;
      },
    };
  }
}
